import React, { useEffect, useState, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PortalMenuWrapperProps {
  children: ReactNode;
  isVisible: boolean;
  triggerRef: React.RefObject<HTMLElement>;
  onClose?: () => void;
  offset?: { x: number; y: number };
}

interface Position {
  top: number;
  left: number;
  transformOrigin: string;
}

export const PortalMenuWrapper: React.FC<PortalMenuWrapperProps> = ({
  children,
  isVisible,
  triggerRef,
  onClose,
  offset = { x: 0, y: 4 }
}) => {
  const [position, setPosition] = useState<Position>({ top: 0, left: 0, transformOrigin: 'top left' });
  const menuRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (!triggerRef.current || !menuRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let top = triggerRect.bottom + offset.y;
    let left = triggerRect.left + offset.x;
    let transformOrigin = 'top left';

    // Check if menu goes beyond right edge
    if (left + menuRect.width > viewport.width) {
      left = triggerRect.right - menuRect.width - offset.x;
      transformOrigin = 'top right';
    }

    // Check if menu goes beyond left edge
    if (left < 0) {
      left = offset.x;
      transformOrigin = 'top left';
    }

    // Check if menu goes beyond bottom edge
    if (top + menuRect.height > viewport.height) {
      top = triggerRect.top - menuRect.height - offset.y;
      transformOrigin = transformOrigin.replace('top', 'bottom');
    }

    // Check if menu goes beyond top edge
    if (top < 0) {
      top = offset.y;
      transformOrigin = transformOrigin.replace('bottom', 'top');
    }

    setPosition({ top, left, transformOrigin });
  };

  useEffect(() => {
    if (isVisible) {
      // Initial calculation
      calculatePosition();
      
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition, true);

      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition, true);
      };
    }
    return undefined; // No cleanup needed when not visible
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, triggerRef.current]);

  useEffect(() => {
    if (isVisible) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node) &&
          triggerRef.current &&
          !triggerRef.current.contains(event.target as Node)
        ) {
          onClose?.();
        }
      };

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose?.();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
    return undefined; // No cleanup needed when not visible
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const portalContent = (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
        transformOrigin: position.transformOrigin,
        animation: isVisible ? 'fadeInScale 0.15s ease-out forwards' : 'fadeOutScale 0.15s ease-in forwards'
      }}
    >
      {children}
    </div>
  );

  return createPortal(portalContent, document.body);
};